/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Tersoff parameter packs for the covalent 'tersoff' force field.
 * Tersoff (PRB 39, 1989, 5566 and earlier) is the canonical bond-order
 * potential for the carbon allotropes, the group-IV semiconductors,
 * and the III-V semiconductors. One functional form covers every
 * tetrahedral + planar covalent hybrid.
 *
 * Parameter packs are keyed by (sorted) composition. Each pack's
 * `build()` returns a TersoffPotential whose internal parameter table
 * is keyed by ORDERED (central, bonded, third) triples — matching the
 * LAMMPS .tersoff file format directly. For an elemental crystal one
 * triple key suffices (Si-Si-Si); for a binary, eight (X-X-X, X-X-Y,
 * X-Y-X, X-Y-Y, Y-X-X, Y-X-Y, Y-Y-X, Y-Y-Y) — exactly the layout of
 * a LAMMPS SiC.tersoff or GaN.tersoff file.
 *
 * The triple layout matters because Tersoff binary fits use per-triple
 * angular parameters that don't simplify to per-element. For SiC the
 * angular only depends on the central atom; for BNC on the (central,
 * bonded) pair; for GaN on the (central, third) pair. The per-triple
 * lookup handles all three uniformly, with no mixing rules.
 *
 * Standard sources, all validated against experimental cohesion at the
 * experimental lattice constant (the test pattern at the bottom of
 * madelungCheck.ts is the validation gate — ship only ≲ 10 %):
 *   - Si elemental: Tersoff (1988) PRB 38, 9902 — "T1" Si, 0.0 % off
 *   - C elemental:  Tersoff (1989) PRB 39, 5566 — C T3 (covers diamond
 *     AND graphite because λ₃=0 strips the radial angular weighting
 *     and lets g_C pick its own preferred angle), 0.0 % off
 *   - Ge elemental: Tersoff (1989) PRB 39, 5566 — Ge T3, 0.0 % off
 *   - β-SiC binary: LAMMPS SiC_Erhart-Albe.tersoff — Erhart & Albe,
 *     PRB 71, 035211 (2005). The accepted modern standard SiC Tersoff
 *     fit; 0.0 % off experimental cohesion at experimental a₀
 *   - c-GaN binary: LAMMPS GaN.tersoff — Nord, Albe, Erhart, Nordlund,
 *     JPCM 15, 5649 (2003). Per-(central, third) angular variation,
 *     0.6 % off
 *   - c-BN / h-BN:  Karaaslan, Yapicioglu, Sevik 2020, PRApplied 13,
 *     034027. PSO-optimized transferable III-N fit, 8.8 % off
 *   - c-AlN / w-AlN: Karaaslan 2020 (same paper), 6.2 % off
 *   - GaAs binary:  LAMMPS 2002_GaAs.tersoff — Albe, Nordlund, Nord,
 *     Kuronen, PRB 66, 035205 (2002), 2.9 % off
 *
 * NOTE on the Si parameter sets: the elemental Si pack uses Tersoff
 * 1988 T1 (A=3264.7, B=95.37, …) while the SiC pack carries its own
 * Si parameters from the Erhart-Albe joint fit (different A, B, c, d,
 * h, …). Each parameter set is internally consistent for its compound;
 * Si entries don't translate across packs.
 *
 * Proxy fits for compositions without a published Tersoff parameterization
 * (Powell 2007 didn't include them, and no widely-cited replacement
 * exists). Each proxy carries an explicit caveat in its docstring and
 * validates within ~15 % of experimental cohesion — usable for
 * pedagogical MD, NOT for quantitative property prediction:
 *   - AlP via Powell AlAs cross-pair (Al cation, anion P→As proxy), 14 % under
 *   - BP via Karaaslan BN cross-pair (B cation, anion P→N proxy), 12 % over
 *   - BAs via Karaaslan BN cross-pair (B cation, anion As→N proxy), 12 % over
 *   - Sn (α-tin) via Ge T3 with physical scaling — A, B scaled by the
 *     Sn/Ge cohesion ratio (0.816), λ scaled by the inverse bond-
 *     length ratio (0.872). Lands at experimental cohesion exactly
 *     because the ratios are self-consistent (the scaled potential's
 *     minimum bond length 2.450/0.872 = 2.810 Å matches Sn's actual
 *     bond, so the rescaled depth gives the rescaled energy).
 *
 * Adding a binary the right way (see git log for the binary-validation
 * smoke pattern):
 *   1. Find the LAMMPS .tersoff file for the compound (or a published
 *      paper supplementary).
 *   2. Paste each row as a TERSOFF_PACKS triple entry verbatim (zeros
 *      and all — non-diagonal rows have placeholder zeros that the
 *      class correctly never reads).
 *   3. Validate cohesion at the experimental lattice constant; accept
 *      only if within ~10 % of experiment.
 */
import { CrystalState } from '../../types.ts';
import { BondOrderPotential, TersoffParams, TersoffPotential } from './PairPotential.ts';

export interface TersoffPack {
  /** Literature source for the parameter set. */
  source: string;
  /** Build the BondOrderPotential for this composition. */
  build(): BondOrderPotential;
  /**
   * Short name for the force-field picker when the pack is not one
   * published fit but several joined (a Si/Ge or GaAs/AlAs stack).
   */
  label?: string;
}

// ── Reusable triple constants. Each is one row of a LAMMPS .tersoff ──
//    file. For non-diagonal triples (k ≠ j), the pair properties (A,
//    B, λ₁, λ₂, n, β) are placeholder zeros — the TersoffPotential
//    class reads them only from the diagonal (i, j, j) row. The R, D
//    in a non-diagonal row is the i-k pair cutoff used for f_C(r_ik).

/** Tersoff (1988) "T1" silicon — elemental Si. */
const SI_T1_SISISI: TersoffParams = {
  A: 3264.7,
  B: 95.373,
  lambda1: 3.2394,
  lambda2: 1.3258,
  beta: 0.33675,
  n: 22.956,
  c: 4.8381,
  d: 2.0417,
  h: 0,
  R: 3.0,
  D: 0.2,
  gamma: 1,
  lambda3: 1.3258,
  m: 3,
};

/** Tersoff (1989) "T3" carbon — elemental diamond + graphite. */
const C_T3_CCC: TersoffParams = {
  A: 1393.6,
  B: 346.7,
  lambda1: 3.4879,
  lambda2: 2.2119,
  beta: 1.5724e-7,
  n: 0.72751,
  c: 38049,
  d: 4.3484,
  h: -0.57058,
  R: 1.95,
  D: 0.15,
  gamma: 1,
  lambda3: 0,
  m: 3,
};

/** Tersoff (1989) "T3" germanium. */
const GE_T3_GEGEGE: TersoffParams = {
  A: 1769.0,
  B: 419.23,
  lambda1: 2.4451,
  lambda2: 1.7047,
  beta: 9.0166e-7,
  n: 0.75627,
  c: 106430,
  d: 15.652,
  h: -0.43884,
  R: 2.95,
  D: 0.15,
  gamma: 1,
  lambda3: 1.7047,
  m: 3,
};

// Si from the SAME paper as the Ge row above — Tersoff (1989) PRB 39,
// 5566, the multicomponent set (the 1988 "T3" Si). The elemental Si pack
// keeps T1 (see the header note); this row exists for the Si/Ge pack,
// because Tersoff's mixing rule and its χ_SiGe were fit between the
// 1989 Si and Ge sets, not T1. R, D are the LAMMPS form of the paper's
// R = 2.7, S = 3.0 Å. Cohesion: −4.63 eV/atom at a₀ = 5.431 Å, 0.0 % off
// (verified in tersoffHetero.test.ts).
const SI_T3_SISISI: TersoffParams = {
  A: 1830.8,
  B: 471.18,
  lambda1: 2.4799,
  lambda2: 1.7322,
  beta: 1.1e-6,
  n: 0.78734,
  c: 100390,
  d: 16.217,
  h: -0.59825,
  R: 2.85,
  D: 0.15,
  gamma: 1,
  lambda3: 1.7322,
  m: 3,
};

/**
 * Tersoff's (1989) mixing rule for one bond, as the row for the CENTRAL
 * atom: the pair terms are means of the two pure-element rows — A, B, R,
 * S geometric (B scaled by the fitted χ), λ₁, λ₂ arithmetic — and the
 * bond-order and angular terms (β, n, c, d, h, γ, m) stay the central
 * atom's own. λ₃ follows the central row's convention: Tersoff's rows
 * (m = 3) set λ₃ = λ₂ of the pair, a zero stays zero, and an Albe-style
 * row (m = 1, λ₃ = 2μ) keeps its own value.
 */
const tersoffMixedRow = (central: TersoffParams, other: TersoffParams, chi: number): TersoffParams => {
  // LAMMPS (R, D) ↔ Tersoff (R, S): R_T = R − D, S_T = R + D.
  const rT = Math.sqrt((central.R - central.D) * (other.R - other.D));
  const sT = Math.sqrt((central.R + central.D) * (other.R + other.D));
  const lambda2 = (central.lambda2 + other.lambda2) / 2;
  return {
    ...central,
    A: Math.sqrt(central.A * other.A),
    B: chi * Math.sqrt(central.B * other.B),
    lambda1: (central.lambda1 + other.lambda1) / 2,
    lambda2,
    R: (rT + sT) / 2,
    D: (sT - rT) / 2,
    lambda3: central.lambda3 === 0 ? 0 : central.m === 3 ? lambda2 : central.lambda3,
  };
};

/**
 * Tersoff's (1989) recipe for a bond between two of his elements, as a
 * full 8-row table (see tersoffMixedRow). A mixed third atom (Si-Si-Ge)
 * takes its angular row from the (central, third) pair, which is also
 * where the kernel reads f_C(r_ik).
 */
const tersoff1989Mixed = (
  eA: string,
  pA: TersoffParams,
  eB: string,
  pB: TersoffParams,
  chi: number,
): Record<string, TersoffParams> => {
  const ab = tersoffMixedRow(pA, pB, chi);
  const ba = tersoffMixedRow(pB, pA, chi);
  return {
    [`${eA}-${eA}-${eA}`]: pA,
    [`${eB}-${eB}-${eB}`]: pB,
    [`${eA}-${eB}-${eB}`]: ab,
    [`${eB}-${eA}-${eA}`]: ba,
    [`${eA}-${eA}-${eB}`]: ab,
    [`${eA}-${eB}-${eA}`]: pA,
    [`${eB}-${eB}-${eA}`]: ba,
    [`${eB}-${eA}-${eB}`]: pB,
  };
};

// ── LAMMPS SiC_Erhart-Albe.tersoff (Erhart & Albe, PRB 71, 035211, 2005) ──
//    Pasted from raw.githubusercontent.com/lammps/lammps/develop/
//    potentials/SiC_Erhart-Albe.tersoff. Each constant maps to one row.
//
//    Why Erhart-Albe over the original Tersoff 1989 SiC fit:
//    Erhart-Albe targeted not just bulk cohesion but also the
//    elastic constants, defect formation energies, surface energies,
//    and the diamond/zincblende polymorph stability — making it the
//    accepted standard for any SiC MD work past 2005. At the
//    experimental a₀ = 4.358 Å it reproduces the experimental cohesion
//    −6.34 eV/atom essentially exactly (−6.339), with its energy
//    minimum sitting at experimental a₀ (Tersoff 1989 in contrast had
//    its minimum slightly contracted to ~4.30 Å).

const SIC_C_C_C: TersoffParams = {
  A: 2019.8449,
  B: 175.426651,
  lambda1: 4.18426232,
  lambda2: 1.93090093,
  beta: 1,
  n: 1,
  c: 181.91,
  d: 6.28433,
  h: -0.5556,
  R: 2.0,
  D: 0.15,
  gamma: 0.11233,
  lambda3: 0,
  m: 1,
};

const SIC_SI_SI_SI: TersoffParams = {
  // Erhart-Albe Si parameters — independently fit for the SiC binary.
  // NOT the same numbers as the elemental Si T1 set above (SI_T1_SISISI),
  // and not the same as the Tersoff 1989 SiC Si either. Each Tersoff
  // paper picks its own internally-consistent Si fit.
  A: 2145.7128,
  B: 219.521624,
  lambda1: 2.83318929,
  lambda2: 1.53810493,
  beta: 1,
  n: 1,
  c: 2.00494,
  d: 0.81472,
  h: -0.259,
  R: 2.82,
  D: 0.14,
  gamma: 0.114354,
  lambda3: 0,
  m: 1,
};

const SIC_SI_SI_C: TersoffParams = {
  // Non-diagonal: pair props (A, B, λ₁, λ₂, n, β) are placeholder 0
  // and never read; angular comes from this row; R, D is i-k = Si-C cutoff.
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 273987,
  d: 180.314,
  h: -0.68,
  R: 2.4,
  D: 0.2,
  gamma: 0.011877,
  lambda3: 0,
  m: 1,
};

const SIC_SI_C_C: TersoffParams = {
  A: 1779.36144,
  B: 225.189481,
  lambda1: 3.26563307,
  lambda2: 1.76807421,
  beta: 1,
  n: 1,
  c: 273987,
  d: 180.314,
  h: -0.68,
  R: 2.4,
  D: 0.2,
  gamma: 0.011877,
  lambda3: 0,
  m: 1,
};

const SIC_C_SI_SI: TersoffParams = {
  A: 1779.36144,
  B: 225.189481,
  lambda1: 3.26563307,
  lambda2: 1.76807421,
  beta: 1,
  n: 1,
  c: 273987,
  d: 180.314,
  h: -0.68,
  R: 2.4,
  D: 0.2,
  gamma: 0.011877,
  lambda3: 0,
  m: 1,
};

const SIC_C_SI_C: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 181.91,
  d: 6.28433,
  h: -0.5556,
  R: 2.0,
  D: 0.15,
  gamma: 0.11233,
  lambda3: 0,
  m: 1,
};

const SIC_C_C_SI: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 273987,
  d: 180.314,
  h: -0.68,
  R: 2.4,
  D: 0.2,
  gamma: 0.011877,
  lambda3: 0,
  m: 1,
};

const SIC_SI_C_SI: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 2.00494,
  d: 0.81472,
  h: -0.259,
  R: 2.82,
  D: 0.14,
  gamma: 0.114354,
  lambda3: 0,
  m: 1,
};

// ── LAMMPS GaN.tersoff (Nord, Albe, Erhart, Nordlund, JPCM 15, 5649 (2003)) ──

const GAN_GA_GA_GA: TersoffParams = {
  A: 535.199,
  B: 410.132,
  lambda1: 1.60916,
  lambda2: 1.4497,
  beta: 1,
  n: 1,
  c: 1.918,
  d: 0.75,
  h: -0.3013,
  R: 2.87,
  D: 0.15,
  gamma: 0.007874,
  lambda3: 1.846,
  m: 1,
};

const GAN_N_N_N: TersoffParams = {
  A: 1044.77,
  B: 423.769,
  lambda1: 3.55779,
  lambda2: 2.38426,
  beta: 1,
  n: 1,
  c: 0.178493,
  d: 0.20172,
  h: -0.045238,
  R: 2.2,
  D: 0.2,
  gamma: 0.76612,
  lambda3: 0,
  m: 1,
};

const GAN_GA_GA_N: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 65.207,
  d: 2.821,
  h: -0.518,
  R: 2.9,
  D: 0.2,
  gamma: 0.001632,
  lambda3: 0,
  m: 1,
};

const GAN_GA_N_N: TersoffParams = {
  A: 6136.44,
  B: 3864.27,
  lambda1: 2.93516,
  lambda2: 2.63906,
  beta: 1,
  n: 1,
  c: 65.207,
  d: 2.821,
  h: -0.518,
  R: 2.9,
  D: 0.2,
  gamma: 0.001632,
  lambda3: 0,
  m: 1,
};

const GAN_N_GA_GA: TersoffParams = {
  A: 6136.44,
  B: 3864.27,
  lambda1: 2.93516,
  lambda2: 2.63906,
  beta: 1,
  n: 1,
  c: 65.207,
  d: 2.821,
  h: -0.518,
  R: 2.9,
  D: 0.2,
  gamma: 0.001632,
  lambda3: 0,
  m: 1,
};

const GAN_N_GA_N: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0.178493,
  d: 0.20172,
  h: -0.045238,
  R: 2.2,
  D: 0.2,
  gamma: 0.76612,
  lambda3: 0,
  m: 1,
};

const GAN_N_N_GA: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 65.207,
  d: 2.821,
  h: -0.518,
  R: 2.9,
  D: 0.2,
  gamma: 0.001632,
  lambda3: 0,
  m: 1,
};

const GAN_GA_N_GA: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 1.918,
  d: 0.75,
  h: -0.3013,
  R: 2.87,
  D: 0.15,
  gamma: 0.007874,
  lambda3: 1.846,
  m: 1,
};

// ── Karaaslan 2020 (Phys. Rev. Applied 13, 034027) transferable Tersoff ──
//    fits for the III-N binaries (BN, AlN, GaN). Unlike Nord/Albe parameter
//    sets, Karaaslan optimized via particle-swarm against bulk structural,
//    mechanical, vibrational AND thermal properties jointly, giving a fit
//    that's good across multiple observables. Sourced from:
//    github.com/by-student-2017/lammps_education_metal_win/blob/master/
//    potentials/tersoff/Karaaslan_2020/{BN,AlN,GaN}.tersoff
//
//    The Karaaslan packs target ONLY the binary compound — pure-element
//    diagonal rows (B-B-B, N-N-N, Al-Al-Al, Ga-Ga-Ga) are all-zero
//    placeholders. This is safe because in zincblende/wurtzite the homo-
//    species NNN distance exceeds the pair cutoff, so those rows are
//    never read at runtime.
const PLACEHOLDER_BN: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0,
  d: 1,
  h: 0,
  R: 2.2,
  D: 0.1,
  gamma: 1,
  lambda3: 0,
  m: 3,
};

const BN_B_N_N: TersoffParams = {
  A: 1205.44629,
  B: 436.75003,
  lambda1: 2.965635,
  lambda2: 2.060658,
  beta: 0.741928e-6,
  n: 1.1568,
  c: 26320.21584,
  d: 5.255691,
  h: -0.87021,
  R: 2.2,
  D: 0.1,
  gamma: 1,
  lambda3: 1.16572,
  m: 3,
};

const BN_N_B_B: TersoffParams = {
  A: 1205.44629,
  B: 436.75003,
  lambda1: 2.965635,
  lambda2: 2.060658,
  beta: 1.335243e-6,
  n: 0.9398,
  c: 27483.938392,
  d: 7.783163,
  h: -0.74974,
  R: 2.2,
  D: 0.1,
  gamma: 1,
  lambda3: 1.10829,
  m: 3,
};

const PLACEHOLDER_ALN_SMALL: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0,
  d: 1,
  h: 0,
  R: 2.2,
  D: 0.1,
  gamma: 1,
  lambda3: 0,
  m: 3,
};
const PLACEHOLDER_ALN_LARGE: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0,
  d: 1,
  h: 0,
  R: 2.55,
  D: 0.15,
  gamma: 1,
  lambda3: 0,
  m: 3,
};

const ALN_AL_N_N: TersoffParams = {
  A: 1258.56726,
  B: 453.22851,
  lambda1: 2.434869,
  lambda2: 1.71768,
  beta: 2.133047e-6,
  n: 0.59823,
  c: 19110.74178,
  d: 10.88209,
  h: -0.97266,
  R: 2.55,
  D: 0.15,
  gamma: 1,
  lambda3: 1.18676,
  m: 3,
};

const ALN_N_AL_AL: TersoffParams = {
  A: 1258.56726,
  B: 453.22851,
  lambda1: 2.434869,
  lambda2: 1.71768,
  beta: 2.496023e-6,
  n: 1.22088,
  c: 27568.039128,
  d: 8.292436,
  h: -0.81623,
  R: 2.55,
  D: 0.15,
  gamma: 1,
  lambda3: 1.10071,
  m: 3,
};

// ── Albe, Nordlund, Nord, Kuronen 2002 (PRB 66, 035205) GaAs ──
//    From the by-student LAMMPS-mirror repo 2002_GaAs.tersoff;
//    values verified by Lucas Hale and matching OpenKIM model
//    MO_799020228312_001. Note the Ga-Ga parameters MATCH the Nord
//    2003 GaN pack's Ga-Ga — Albe used a consistent Ga parameterization
//    across his III-V family.
const GAAS_GA_GA_GA: TersoffParams = {
  A: 535.1992682033992,
  B: 410.13170048006185,
  lambda1: 1.6091637579811449,
  lambda2: 1.4496970792622927,
  beta: 1,
  n: 1,
  c: 1.918,
  d: 0.75,
  h: -0.3013,
  R: 2.95,
  D: 0.15,
  gamma: 0.007874,
  lambda3: 1.846,
  m: 1,
};

const GAAS_AS_AS_AS: TersoffParams = {
  A: 1539.626646460118,
  B: 194.90209095351517,
  lambda1: 2.767727768404978,
  lambda2: 1.48802568193816,
  beta: 1,
  n: 1,
  c: 0.1186,
  d: 0.1612,
  h: -0.07748,
  R: 3.4,
  D: 0.2,
  gamma: 0.455,
  lambda3: 3.161,
  m: 1,
};

const GAAS_AS_AS_GA: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 1.29,
  d: 0.56,
  h: -0.237,
  R: 3.1,
  D: 0.2,
  gamma: 0.0166,
  lambda3: 0,
  m: 1,
};

const GAAS_AS_GA_AS: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 0.1186,
  d: 0.1612,
  h: -0.07748,
  R: 3.4,
  D: 0.2,
  gamma: 0.455,
  lambda3: 3.161,
  m: 1,
};

const GAAS_AS_GA_GA: TersoffParams = {
  A: 3306.168457081707,
  B: 1929.3021521674843,
  lambda1: 2.3010913851162016,
  lambda2: 2.0154956513236417,
  beta: 1,
  n: 1,
  c: 1.29,
  d: 0.56,
  h: -0.237,
  R: 3.1,
  D: 0.2,
  gamma: 0.0166,
  lambda3: 0,
  m: 1,
};

const GAAS_GA_AS_AS: TersoffParams = {
  A: 3306.168457081707,
  B: 1929.3021521674843,
  lambda1: 2.3010913851162016,
  lambda2: 2.0154956513236417,
  beta: 1,
  n: 1,
  c: 1.29,
  d: 0.56,
  h: -0.237,
  R: 3.1,
  D: 0.2,
  gamma: 0.0166,
  lambda3: 0,
  m: 1,
};

const GAAS_GA_AS_GA: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 1.918,
  d: 0.75,
  h: -0.3013,
  R: 2.95,
  D: 0.15,
  gamma: 0.007874,
  lambda3: 1.846,
  m: 1,
};

const GAAS_GA_GA_AS: TersoffParams = {
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 0,
  c: 1.29,
  d: 0.56,
  h: -0.237,
  R: 3.1,
  D: 0.2,
  gamma: 0.0166,
  lambda3: 0,
  m: 1,
};

// ── Powell, Migliorato, Cullis (2007) PRB 75, 115202 — a CONSISTENT ──
//    Tersoff parameter set for the III-V semiconductor family. Same
//    functional form, same fitting strategy across GaAs, InP, InAs,
//    GaP, AlAs, GaSb, InSb, AlSb — designed for transferable use
//    across ternary alloys (InGaAs, AlGaAs, etc.) when those become
//    interesting. Files mirrored at:
//    github.com/by-student-2017/lammps_education_metal_win/blob/master/
//    potentials/tersoff/Powell_2007/{InP,InAs,GaP,AlAs,GaAs,GaSb,
//    InSb,AlSb}.tersoff
//
//    Pure-element diagonals (X-X-X, Y-Y-Y) are all-zero placeholders.
//    In zincblende the homo-species NNN distance exceeds the pair
//    cutoff for every binary here, so these rows are never read and
//    the placeholders contribute nothing.
//
//    `powellBinary` collapses each binary's 8 (i,j,k) triples to one
//    constructor call carrying only the unique cross-pair physics.
type PowellPairPhysics = Pick<
  TersoffParams,
  'A' | 'B' | 'lambda1' | 'lambda2' | 'beta' | 'n' | 'c' | 'd' | 'h' | 'gamma' | 'lambda3' | 'm'
>;
const powellPlaceholder = (R: number, D: number): TersoffParams => ({
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0,
  d: 1,
  h: 0,
  R,
  D,
  gamma: 1,
  lambda3: 0,
  m: 3,
});
const powellBinary = (
  eA: string,
  eB: string,
  RA: number,
  DA: number, // pure-A diagonal cutoff (file's X-X-X row)
  RB: number,
  DB: number, // pure-B diagonal cutoff
  RAB: number,
  DAB: number, // cross-pair A-B cutoff (file's X-Y-Y row)
  cross: PowellPairPhysics,
): Record<string, TersoffParams> => {
  const crossFull: TersoffParams = { ...cross, R: RAB, D: DAB };
  const phCross = powellPlaceholder(RAB, DAB);
  return {
    [`${eA}-${eA}-${eA}`]: powellPlaceholder(RA, DA),
    [`${eB}-${eB}-${eB}`]: powellPlaceholder(RB, DB),
    [`${eA}-${eB}-${eB}`]: crossFull,
    [`${eB}-${eA}-${eA}`]: crossFull,
    [`${eA}-${eB}-${eA}`]: phCross,
    [`${eA}-${eA}-${eB}`]: phCross,
    [`${eB}-${eA}-${eB}`]: phCross,
    [`${eB}-${eB}-${eA}`]: phCross,
  };
};

// Each call below mirrors one of the LAMMPS Powell_2007 .tersoff files
// row-for-row. Cross-pair physics is the unique part; the 8 triples
// reduce to one call thanks to `powellBinary`.
const POWELL_GAAS_TRIPLES = powellBinary('Ga', 'As', 3.5, 0.1, 3.5, 0.1, 3.5, 0.1, {
  A: 446079.3317,
  B: 16.03046979,
  lambda1: 5.84464068,
  lambda2: 0.839717,
  beta: 0.257183,
  n: 3.55586,
  c: 2.16345,
  d: 0.750147,
  h: -0.448899,
  gamma: 1,
  lambda3: 0.244341,
  m: 3,
});
const POWELL_INP_TRIPLES = powellBinary('In', 'P', 3.3, 0.1, 3.3, 0.1, 3.3, 0.1, {
  A: 2979.859085,
  B: 404.713323,
  lambda1: 2.716484,
  lambda2: 1.718098,
  beta: 0.338811,
  n: 5.3449,
  c: 1.2179,
  d: 0.831026,
  h: -0.461576,
  gamma: 1,
  lambda3: 1.89106,
  m: 3,
});
const POWELL_INAS_TRIPLES = powellBinary('In', 'As', 3.7, 0.1, 3.7, 0.1, 3.7, 0.1, {
  A: 2866.660922,
  B: 809.217956,
  lambda1: 2.44216742,
  lambda2: 1.839605,
  beta: 0.139688,
  n: 2.35072,
  c: 2.10031,
  d: 0.827636,
  h: -0.442115,
  gamma: 1,
  lambda3: 0.140874,
  m: 3,
});
const POWELL_GAP_TRIPLES = powellBinary('Ga', 'P', 3.1, 0.1, 3.1, 0.1, 3.1, 0.1, {
  A: 2806.027777,
  B: 378.0635108,
  lambda1: 2.8991224,
  lambda2: 1.818646,
  beta: 0.339811,
  n: 5.42195,
  c: 1.26417,
  d: 0.799535,
  h: -0.433247,
  gamma: 1,
  lambda3: 1.81771,
  m: 3,
});
const POWELL_ALAS_TRIPLES = powellBinary('Al', 'As', 3.7, 0.1, 3.7, 0.1, 3.7, 0.1, {
  A: 1780.771194,
  B: 330.99221,
  lambda1: 2.5431003,
  lambda2: 1.64996,
  beta: 0.359702,
  n: 8.36992,
  c: 1.05025,
  d: 0.850097,
  h: -0.458041,
  gamma: 1,
  lambda3: 1.38402,
  m: 3,
});
const POWELL_GASB_TRIPLES = powellBinary('Ga', 'Sb', 3.5, 0.1, 3.5, 0.1, 3.5, 0.1, {
  A: 2521.756902,
  B: 544.90392,
  lambda1: 2.502449,
  lambda2: 1.745168,
  beta: 0.363018,
  n: 4.60221,
  c: 1.20875,
  d: 0.839761,
  h: -0.427706,
  gamma: 1,
  lambda3: 0.968688,
  m: 3,
});
const POWELL_INSB_TRIPLES = powellBinary('In', 'Sb', 3.7, 0.1, 3.7, 0.1, 3.7, 0.1, {
  A: 14105.27888,
  B: 67.95917,
  lambda1: 3.5372944,
  lambda2: 1.168874,
  beta: 0.304347,
  n: 1.8926,
  c: 5.32122,
  d: 1.39907,
  h: -0.489527,
  gamma: 1,
  lambda3: 2.47677,
  m: 3,
});
const POWELL_ALSB_TRIPLES = powellBinary('Al', 'Sb', 3.7, 0.1, 3.7, 0.1, 3.5, 0.1, {
  A: 42112.56157,
  B: 198.13166,
  lambda1: 4.0187227,
  lambda2: 1.504514,
  beta: 0.604408,
  n: 122.43,
  c: 3.44865,
  d: 1.20819,
  h: -0.210583,
  gamma: 1,
  lambda3: 0.95556,
  m: 3,
});

// ── PROXY fits — no published Powell-style Tersoff fit available; the ──
//    parameters below are SUBSTITUTED from chemically-adjacent binaries
//    rather than fit to the target compound's own DFT data. They give
//    qualitatively-correct zincblende cohesion (within ~15 % of
//    experiment at the experimental lattice constant), which is enough
//    to keep these crystals from collapsing under generic LJ. They are
//    NOT suitable for quantitative property prediction (elastic
//    constants, phonons, defect energies); replace with a real fit
//    if/when one becomes available.

// AlP — proxy from Powell AlAs cross-pair. The Al cation stays;
// As→P is the anion substitution. P is smaller and more electronegative
// than As, so the real Al-P bond is shorter (2.37 vs 2.45 Å). At the
// experimental AlP lattice constant a₀ = 5.467 Å this proxy gives
// E_coh ≈ −3.70 eV/atom vs experimental −4.3 (14 % under).
const PROXY_ALP_TRIPLES = powellBinary('Al', 'P', 3.4, 0.1, 3.4, 0.1, 3.4, 0.1, {
  A: 1780.771194,
  B: 330.99221,
  lambda1: 2.5431003,
  lambda2: 1.64996,
  beta: 0.359702,
  n: 8.36992,
  c: 1.05025,
  d: 0.850097,
  h: -0.458041,
  gamma: 1,
  lambda3: 1.38402,
  m: 3,
});

// BP, BAs — proxy from Karaaslan 2020 BN. B-N is the strongest bond
// in the III-V family, much shorter than B-P (1.97) or B-As (2.07);
// substituting the anion sets a cohesion that turns out within ~12 %
// of experiment for both target compounds at their respective lattice
// constants. The asymmetry between B-X-X and X-B-B cross rows is
// preserved from Karaaslan; cutoff R is bumped from BN's 2.2 Å to
// accommodate the longer B-P / B-As bonds.
const proxyBNForBX = (eNew: string, R: number): Record<string, TersoffParams> => {
  const phB = { ...PLACEHOLDER_BN, R, D: 0.1 };
  const ph = { ...PLACEHOLDER_BN, R, D: 0.1 };
  return {
    'B-B-B': phB,
    [`${eNew}-${eNew}-${eNew}`]: phB,
    [`B-${eNew}-${eNew}`]: { ...BN_B_N_N, R },
    [`${eNew}-B-B`]: { ...BN_N_B_B, R },
    [`B-${eNew}-B`]: ph,
    [`B-B-${eNew}`]: ph,
    [`${eNew}-B-${eNew}`]: ph,
    [`${eNew}-${eNew}-B`]: ph,
  };
};
const PROXY_BP_TRIPLES = proxyBNForBX('P', 2.6);
const PROXY_BAS_TRIPLES = proxyBNForBX('As', 2.7);

// α-Sn (gray tin) — proxy from Ge T3 (Tersoff 1989) with physical
// scaling. Sn is the next group-IV element after Ge in the diamond-
// cubic family, structurally identical but with longer bonds (2.810
// vs Ge's 2.450 Å) and weaker cohesion (-3.14 vs -3.85 eV/atom).
// Apply the natural Tersoff scaling rules:
//   - A, B scale with cohesion → ×0.816  (Sn/Ge energy ratio)
//   - λ₁, λ₂, λ₃ scale with inverse bond length → ×0.872 (Ge/Sn ratio)
//   - R bumped to 3.4 to clear Sn's longer NN bond (Ge used R = 2.95)
//   - β, n, c, d, h kept from Ge unchanged — these are angular and
//     bond-order shape parameters that don't have an obvious scaling.
// The two scalings happen to be self-consistent: at the rescaled λ,
// the potential's bond-length minimum is 2.450/0.872 = 2.810 Å —
// exactly Sn's experimental bond. So the rescaled A, B at that bond
// length give the rescaled cohesion exactly. Lands at −3.140 eV/atom
// vs target −3.14 (essentially zero error) at experimental a₀ =
// 6.4892 Å. Confidence is qualitative; replace if a real fit ships.
const SN_PROXY_SCALE_AB = 3.14 / 3.85; // ≈ 0.816
const SN_PROXY_SCALE_LAMBDA = 2.45 / 2.81; // ≈ 0.872
const SN_PROXY: TersoffParams = {
  A: 1769.0 * SN_PROXY_SCALE_AB,
  B: 419.23 * SN_PROXY_SCALE_AB,
  lambda1: 2.4451 * SN_PROXY_SCALE_LAMBDA,
  lambda2: 1.7047 * SN_PROXY_SCALE_LAMBDA,
  beta: 9.0166e-7,
  n: 0.75627,
  c: 106430,
  d: 15.652,
  h: -0.43884,
  R: 3.4,
  D: 0.15,
  gamma: 1,
  lambda3: 1.7047 * SN_PROXY_SCALE_LAMBDA,
  m: 3,
};

/**
 * Registry keyed by composition. Each pack's `build()` returns a
 * TersoffPotential with all (i, j, k) triples pre-populated.
 *
 * Experimental cohesion / lattice constant for sanity:
 *   C  (diamond):       a₀ = 3.567 Å, E_coh = −7.37 eV/atom
 *   Si (diamond):       a₀ = 5.431 Å, E_coh = −4.63 eV/atom
 *   Ge (diamond):       a₀ = 5.658 Å, E_coh = −3.85 eV/atom
 *   β-SiC (zincblende): a₀ = 4.358 Å, E_coh = −6.34 eV/atom
 *   c-GaN (zincblende): a₀ ≈ 4.50 Å, E_coh ≈ −4.50 eV/atom
 */
export const TERSOFF_PACKS: Record<string, TersoffPack> = {
  // ── Elemental tetrahedral semiconductors ─────────────────────────
  Si: {
    source: 'Tersoff (1988), PRB 38, 9902 — Si T1',
    build: () => new TersoffPotential({ 'Si-Si-Si': SI_T1_SISISI }),
  },
  C: {
    source: 'Tersoff (1989), PRB 39, 5566 — C T3 (diamond + graphite)',
    build: () => new TersoffPotential({ 'C-C-C': C_T3_CCC }),
  },
  Ge: {
    source: 'Tersoff (1989), PRB 39, 5566 — Ge T3',
    build: () => new TersoffPotential({ 'Ge-Ge-Ge': GE_T3_GEGEGE }),
  },
  // Si/Ge — a SiGe alloy or a Si-on-Ge stack — by Tersoff's own mixing
  // rule between his 1989 Si and Ge sets, χ_SiGe = 1.00061 (the alloy
  // mixes almost ideally). Pure-Si and pure-Ge regions see the 1989 rows
  // exactly; only Si–Ge bonds use the mixed pair.
  'Ge,Si': {
    source: "Tersoff (1989), PRB 39, 5566 — Si and Ge T3 with the paper's mixing rule, χ_SiGe = 1.00061",
    label: 'Si/Ge — Tersoff 1989',
    build: () => new TersoffPotential(tersoff1989Mixed('Si', SI_T3_SISISI, 'Ge', GE_T3_GEGEGE, 1.00061)),
  },

  // ── Binary covalent compounds (LAMMPS .tersoff verbatim) ─────────
  // β-SiC — Erhart & Albe (2005), PRB 71, 035211. The accepted modern
  // standard SiC Tersoff fit (post-2005); reproduces experimental
  // cohesion −6.34 eV/atom essentially exactly and has its energy
  // minimum at the experimental a₀ = 4.358 Å.
  //
  // The Si parameters in this pack are NOT the elemental T1 set above —
  // Erhart-Albe refit Si jointly with C for the SiC binary, producing
  // values internally consistent with their Si-C cross terms.
  'C,Si': {
    source: 'LAMMPS SiC_Erhart-Albe.tersoff — Erhart & Albe, PRB 71, 035211 (2005)',
    build: () =>
      new TersoffPotential({
        'C-C-C': SIC_C_C_C,
        'Si-Si-Si': SIC_SI_SI_SI,
        'Si-Si-C': SIC_SI_SI_C,
        'Si-C-C': SIC_SI_C_C,
        'C-Si-Si': SIC_C_SI_SI,
        'C-Si-C': SIC_C_SI_C,
        'C-C-Si': SIC_C_C_SI,
        'Si-C-Si': SIC_SI_C_SI,
      }),
  },

  // GaN — Nord, Albe, Erhart, Nordlund 2003. Uses per-(central, third)
  // angular variation: g changes based on the third atom k, not just
  // central i. Hence eight distinct triple entries. Slightly better
  // cohesion match than Karaaslan 2020 (0.6 % vs 2.4 % off c-GaN).
  'Ga,N': {
    source: 'LAMMPS GaN.tersoff — Nord, Albe, Erhart, Nordlund, JPCM 15, 5649 (2003)',
    build: () =>
      new TersoffPotential({
        'Ga-Ga-Ga': GAN_GA_GA_GA,
        'N-N-N': GAN_N_N_N,
        'Ga-Ga-N': GAN_GA_GA_N,
        'Ga-N-N': GAN_GA_N_N,
        'N-Ga-Ga': GAN_N_GA_GA,
        'N-Ga-N': GAN_N_GA_N,
        'N-N-Ga': GAN_N_N_GA,
        'Ga-N-Ga': GAN_GA_N_GA,
      }),
  },

  // c-BN / h-BN — Karaaslan, Yapicioglu, Sevik 2020. One pack covers
  // both polymorphs because Tersoff's g-function doesn't fix a preferred
  // angle. Reproduces c-BN cohesion within ~9 % of experimental −6.66.
  // The Albe 1998 BN parameters would be more accurate for bulk cBN
  // specifically but are paywall-locked; Karaaslan is the best publicly-
  // available LAMMPS Tersoff fit.
  'B,N': {
    source: 'Karaaslan, Yapicioglu, Sevik (2020), PRApplied 13, 034027 — transferable BN',
    build: () =>
      new TersoffPotential({
        'B-B-B': PLACEHOLDER_BN,
        'N-N-N': PLACEHOLDER_BN,
        'B-B-N': PLACEHOLDER_BN,
        'B-N-B': PLACEHOLDER_BN,
        'N-B-N': PLACEHOLDER_BN,
        'N-N-B': PLACEHOLDER_BN,
        'B-N-N': BN_B_N_N,
        'N-B-B': BN_N_B_B,
      }),
  },

  // c-AlN / w-AlN — Karaaslan, Yapicioglu, Sevik 2020. Reproduces c-AlN
  // cohesion within ~6 % of experimental −5.85. Wurtzite AlN should be
  // similar (Tersoff doesn't fix the angle, and wurtzite vs zincblende
  // cohesion typically differ by < 1 % for the III-Ns).
  'Al,N': {
    source: 'Karaaslan, Yapicioglu, Sevik (2020), PRApplied 13, 034027 — transferable AlN',
    build: () =>
      new TersoffPotential({
        'Al-Al-Al': PLACEHOLDER_ALN_SMALL,
        'N-N-N': PLACEHOLDER_ALN_SMALL,
        'Al-Al-N': PLACEHOLDER_ALN_LARGE,
        'Al-N-Al': PLACEHOLDER_ALN_LARGE,
        'N-Al-N': PLACEHOLDER_ALN_LARGE,
        'N-N-Al': PLACEHOLDER_ALN_LARGE,
        'Al-N-N': ALN_AL_N_N,
        'N-Al-Al': ALN_N_AL_AL,
      }),
  },

  // GaAs zincblende — Albe, Nordlund, Nord, Kuronen 2002, PRB 66, 035205.
  // The de-facto standard GaAs-specific Tersoff parameter set. Chosen
  // over Powell 2007's GaAs entry (which uses an extreme A = 4.5e5)
  // because Albe 2002 was fit specifically for GaAs and validates 2.9 %
  // off experimental cohesion. Powell 2007's value is unphysical at
  // the experimental lattice (gave > 100 % off in our validation).
  'As,Ga': {
    source: 'LAMMPS 2002_GaAs.tersoff — Albe, Nordlund, Nord, Kuronen, PRB 66, 035205 (2002)',
    build: () =>
      new TersoffPotential({
        'Ga-Ga-Ga': GAAS_GA_GA_GA,
        'As-As-As': GAAS_AS_AS_AS,
        'As-As-Ga': GAAS_AS_AS_GA,
        'As-Ga-As': GAAS_AS_GA_AS,
        'As-Ga-Ga': GAAS_AS_GA_GA,
        'Ga-As-As': GAAS_GA_AS_AS,
        'Ga-As-Ga': GAAS_GA_AS_GA,
        'Ga-Ga-As': GAAS_GA_GA_AS,
      }),
  },

  // The Powell, Migliorato, Cullis (2007) consistent III-V family. Each
  // pack reproduces its experimental cohesion to within ~5 % at the
  // experimental zincblende lattice constant. See `powellBinary` above
  // and the header docstring for the architecture; the constants below
  // mirror the LAMMPS Powell_2007 .tersoff files row-for-row.
  'In,P': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_INP_TRIPLES),
  },
  'As,In': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_INAS_TRIPLES),
  },
  'Ga,P': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_GAP_TRIPLES),
  },
  'Al,As': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_ALAS_TRIPLES),
  },
  'Ga,Sb': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_GASB_TRIPLES),
  },
  'In,Sb': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_INSB_TRIPLES),
  },
  'Al,Sb': {
    source: 'Powell, Migliorato, Cullis (2007), PRB 75, 115202 — consistent III-V family',
    build: () => new TersoffPotential(POWELL_ALSB_TRIPLES),
  },

  // PROXY fits — see header note. Cohesion within ~15 % of experiment;
  // not suitable for quantitative property work.
  'Al,P': {
    source: 'PROXY: Powell AlAs cross-pair with anion As→P (no published AlP Tersoff fit)',
    build: () => new TersoffPotential(PROXY_ALP_TRIPLES),
  },
  'B,P': {
    source: 'PROXY: Karaaslan BN cross-pair with anion N→P (no published BP Tersoff fit)',
    build: () => new TersoffPotential(PROXY_BP_TRIPLES),
  },
  'As,B': {
    source: 'PROXY: Karaaslan BN cross-pair with anion N→As (no published BAs Tersoff fit)',
    build: () => new TersoffPotential(PROXY_BAS_TRIPLES),
  },
  Sn: {
    source: 'PROXY: Ge T3 (Tersoff 1989) rescaled by Sn/Ge cohesion (0.816) and bond-length (0.872) ratios',
    build: () => new TersoffPotential({ 'Sn-Sn-Sn': SN_PROXY }),
  },
};

/** Composition key for a crystal: sorted unique basis element symbols. */
const compositionKey = (crystal: CrystalState): string =>
  [...new Set(crystal.basis.map((b) => b.element))].sort().join(',');

// ── Layered stacks: a pack composed from the fits it is made of ──────────
//    A Semiconductor Design device (GaAs on AlAs, GaAs on Si, GaN on SiC…)
//    has a composition no single fit covers. The full n³ triple table is
//    built from the registered packs, pair by pair:
//      • a pair with a registered binary (Ga–As, Si–C, Si–Ge) uses that
//        binary's rows, so each layer sees exactly the fit it would get
//        on its own;
//      • a same-type pair of zincblende ions (Ga–Al, As–P) never bonds in
//        the crystal: an inert row (A = B = 0) keeps the kernel's lookups
//        total;
//      • any other pair (Si–Ga, Si–As, Ge–As, C–N) is a PROXY: Tersoff's
//        1989 mixing rule between the two pure-element rows, χ = 1
//        (tersoffMixedRow). That needs a real pure row on both sides —
//        Si, C, Ge, Sn from their elemental packs, Ga, As, N from Albe
//        2002 / Nord 2003, which fit the elements too. Powell 2007 and the
//        proxy binaries left In, Al, P, Sb, B inert, so a stack that puts
//        one of those against another family (InP on Si) still gets no
//        pack and stays view-only: an interface whose atoms silently did
//        not bond would be worse than no simulation;
//      • a triple spanning two fits (central As, bonded Ga, third Si)
//        takes the angular row of the BOND's own pair (As-Ga-Ga): the
//        third atom is weighted as if it were another Ga. The ζ_ij sum
//        feeds b_ij through the bond's β and n, and each fit sets the scale
//        of its g·ω terms to its own β, n — Albe 2002 is Brenner-style
//        (γ ≈ 0.01, n = 1), Powell 2007 has γ = 1 and n ≈ 3–8. Summing
//        terms of two scales into one ζ collapsed b_ij: the first attempt,
//        angular from the (central, third) pair, put a GaAs/AlAs
//        superlattice at −0.99 eV/atom between end members of −3.35 and
//        −3.78. The cutoff f_C(r_ik) still comes from the i–k pair's row;
//      • the pure rows (Ga-Ga-Ga) differ between fits — a real one (A > 0)
//        is preferred, then the wider cutoff, then the elemental pack.
//    Validated by tersoffHetero.test.ts: pure layers reproduce their own
//    packs exactly, a GaAs/AlAs superlattice and a Ge/GaAs superlattice
//    land between their end members, forces match the numerical gradient,
//    and built devices (GaAs/AlAs, Si/Ge, GaAs on Si, Ge on GaAs) run
//    stable MD.
const ZB_CATIONS = new Set(['B', 'Al', 'Ga', 'In']);
const ZB_ANIONS = new Set(['N', 'P', 'As', 'Sb']);
const inertRow = (R: number, D: number): TersoffParams => ({
  A: 0,
  B: 0,
  lambda1: 0,
  lambda2: 0,
  beta: 0,
  n: 1,
  c: 0,
  d: 1,
  h: 0,
  R,
  D,
  gamma: 1,
  lambda3: 0,
  m: 3,
});
const pairKey = (x: string, y: string): string => [x, y].sort().join(',');

const sameTypeIons = (x: string, y: string): boolean =>
  (ZB_CATIONS.has(x) && ZB_CATIONS.has(y)) || (ZB_ANIONS.has(x) && ZB_ANIONS.has(y));

const composeTersoffPack = (elements: string[]): TersoffPack | null => {
  if (elements.length < 2) return null;
  const rowsOf = (key: string): { source: string; rows: ReadonlyMap<string, TersoffParams> } | null => {
    const pack = TERSOFF_PACKS[key];
    if (!pack) return null;
    const built = pack.build();
    return built instanceof TersoffPotential ? { source: pack.source, rows: built.rows() } : null;
  };
  const binaries = new Map<string, { source: string; rows: ReadonlyMap<string, TersoffParams> }>();
  for (let a = 0; a < elements.length; a++) {
    for (let b = a + 1; b < elements.length; b++) {
      const key = pairKey(elements[a], elements[b]);
      const binary = rowsOf(key);
      if (binary) binaries.set(key, binary);
    }
  }
  // The pure row of an element: a real one from a binary it is in, else the
  // widest, else its elemental pack. An element none of them know ends the
  // composition — it is not a Tersoff element.
  const pureRows = new Map<string, TersoffParams>();
  for (const i of elements) {
    let best: TersoffParams | null = null;
    for (const b of binaries.values()) {
      const row = b.rows.get(`${i}-${i}-${i}`);
      if (!row) continue;
      const real = row.A > 0;
      const bestReal = (best?.A ?? 0) > 0;
      if (!best || (real && !bestReal) || (real === bestReal && row.R + row.D > best.R + best.D)) best = row;
    }
    if (!best || best.A <= 0) best = rowsOf(i)?.rows.get(`${i}-${i}-${i}`) ?? best;
    if (!best) return null;
    pureRows.set(i, best);
  }
  const pureRow = (i: string): TersoffParams => pureRows.get(i)!;
  // The row for the i–j bond with i central: the binary's, inert for
  // same-type ions, else the mixing-rule proxy — which needs both pure rows
  // real. A pair that is none of these ends the composition.
  const proxies: string[] = [];
  const diagonals = new Map<string, TersoffParams>();
  for (const i of elements) {
    for (const j of elements) {
      if (i === j) continue;
      const pi = pureRow(i);
      const binary = binaries.get(pairKey(i, j))?.rows.get(`${i}-${j}-${j}`);
      let row: TersoffParams | undefined = binary;
      if (!row && sameTypeIons(i, j)) row = inertRow(pi.R, pi.D);
      if (!row && pi.A > 0 && pureRow(j).A > 0) {
        row = tersoffMixedRow(pi, pureRow(j), 1);
        if (i < j) proxies.push(`${i}–${j}`);
      }
      if (!row) return null;
      diagonals.set(`${i}-${j}`, row);
    }
  }
  const diagonal = (i: string, j: string): TersoffParams => (i === j ? pureRow(i) : diagonals.get(`${i}-${j}`)!);
  const table: Record<string, TersoffParams> = {};
  for (const i of elements) {
    for (const j of elements) {
      for (const k of elements) {
        const key = `${i}-${j}-${k}`;
        const distinct = new Set([i, j, k]);
        let row: TersoffParams | undefined;
        if (distinct.size === 1) row = pureRow(i);
        else if (distinct.size === 2) {
          const [x, y] = [...distinct];
          row = binaries.get(pairKey(x, y))?.rows.get(key);
        }
        table[key] = row ?? diagonal(i, j);
      }
    }
  }
  const formula = (key: string): string => {
    const [p, q] = key.split(',');
    return ZB_CATIONS.has(p) ? p + q : q + p;
  };
  const inBinary = new Set([...binaries.keys()].flatMap((k) => k.split(',')));
  const names = [...[...binaries.keys()].map(formula), ...elements.filter((e) => !inBinary.has(e))];
  const sources = [...binaries].map(([k, b]) => `${formula(k)}: ${b.source}`);
  for (const e of elements) if (!inBinary.has(e)) sources.push(`${e}: ${TERSOFF_PACKS[e]?.source ?? 'pure row'}`);
  return {
    label: `${names.join('/')} — Tersoff, composed${proxies.length ? ', proxy interface' : ''}`,
    source:
      `Composed for a ${names.join('/')} stack from the fits: ` +
      sources.join('; ') +
      (proxies.length
        ? `. PROXY cross pairs ${proxies.join(', ')}: Tersoff 1989 mixing rule between the pure-element rows, χ = 1 (no published fit)`
        : '') +
      ". Triples that span two fits take the bond's own angular row.",
    build: () => new TersoffPotential(table),
  };
};

const composedPacks = new Map<string, TersoffPack | null>();

/**
 * The Tersoff pack for `crystal`: a registered one for its composition,
 * else one composed from the registered fits (a layered stack, with
 * mixing-rule proxies for the cross pairs no fit covers), else null.
 */
export const tersoffPackFor = (crystal: CrystalState): TersoffPack | null => {
  const key = compositionKey(crystal);
  const direct = TERSOFF_PACKS[key];
  if (direct) return direct;
  if (!composedPacks.has(key)) composedPacks.set(key, key ? composeTersoffPack(key.split(',')) : null);
  return composedPacks.get(key) ?? null;
};

/**
 * The Tersoff pack for a HYDROGEN-TERMINATED finite nanostructure (a
 * passivated dot, wire or ribbon): the pack of its heavy elements, looked
 * up with the capping hydrogens left out. Tersoff has no hydrogen rows, so
 * the covalent force field runs the bond-order potential among the heavy
 * atoms and holds each cap on a harmonic X–H bond (crystalForceFields.ts).
 * Only finite nanostructures qualify: a bulk hydride (silane, germane …)
 * is a molecular crystal with a model of its own and must not be read as
 * capped silicon. Null when the crystal has no hydrogen or no heavy pack.
 */
export const cappedTersoffPackFor = (crystal: CrystalState): TersoffPack | null => {
  if (!crystal.finite && !crystal.nanostructure) return null;
  const elements = [...new Set(crystal.basis.map((b) => b.element))];
  if (!elements.includes('H')) return null;
  const heavy = elements.filter((e) => e !== 'H');
  if (heavy.length === 0) return null;
  return tersoffPackFor({ ...crystal, basis: heavy.map((e) => ({ element: e, fx: 0, fy: 0, fz: 0, label: e })) });
};
